import { useMemo, useState } from "react"
import { ChevronDown, ChevronRight, Download, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog"
import { useLocale } from "./LocaleProvider"
import { useWorkerActivityHistory } from "../lib/queries"
import { formatDuration, formatHours, formatTime, shortDate, windowSeconds } from "../lib/format"
import type { WorkerDailyRecord } from "../lib/api"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import { loadPdfFont } from "../lib/pdf"

const RANGES = [7, 30, 90]

interface WorkerActivityReportModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface OperatorDay {
  date: string
  seconds: number
  heartbeatCount: number
  firstSeenAt: string | null
  lastSeenAt: string | null
}

interface OperatorReport {
  userId: number
  fullName: string
  email: string
  activeDays: number
  totalSeconds: number
  totalHeartbeats: number
  avgSecondsPerDay: number
  days: OperatorDay[]
}

type ReportSortKey = "requests" | "hours" | "avg" | "activeDays"
type SortDir = "asc" | "desc"

function metricValue(op: OperatorReport, key: ReportSortKey): number {
  switch (key) {
    case "activeDays":
      return op.activeDays
    case "hours":
      return op.totalSeconds
    case "avg":
      return op.avgSecondsPerDay
    case "requests":
    default:
      return op.totalHeartbeats
  }
}

function WorkerActivityReportModal({ open, onOpenChange }: WorkerActivityReportModalProps) {
  const { t } = useLocale()
  const [range, setRange] = useState(30)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [sortKey, setSortKey] = useState<ReportSortKey>("requests")
  const [sortDir, setSortDir] = useState<SortDir>("desc")
  const { data: history, isLoading } = useWorkerActivityHistory(open ? range : 0, open)

  const toggleSort = (key: ReportSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"))
    } else {
      setSortKey(key)
      setSortDir("desc")
    }
  }

  const operators: OperatorReport[] = useMemo(() => {
    const rows: WorkerDailyRecord[] = history ?? []
    const byUser = new Map<number, OperatorReport>()

    for (const r of rows) {
      let op = byUser.get(r.userId)
      if (!op) {
        op = {
          userId: r.userId,
          fullName: r.fullName,
          email: r.email,
          activeDays: 0,
          totalSeconds: 0,
          totalHeartbeats: 0,
          avgSecondsPerDay: 0,
          days: [],
        }
        byUser.set(r.userId, op)
      }
      const seconds = windowSeconds(r.firstSeenAt, r.lastSeenAt)
      if (r.heartbeatCount > 0) op.activeDays += 1
      op.totalSeconds += seconds
      op.totalHeartbeats += r.heartbeatCount
      op.days.push({
        date: r.date,
        seconds,
        heartbeatCount: r.heartbeatCount,
        firstSeenAt: r.firstSeenAt,
        lastSeenAt: r.lastSeenAt,
      })
    }

    const list = Array.from(byUser.values())
    for (const op of list) {
      op.avgSecondsPerDay = op.activeDays > 0 ? op.totalSeconds / op.activeDays : 0
      op.days.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
    }
    return list
  }, [history])

  const sorted = useMemo(() => {
    const factor = sortDir === "asc" ? 1 : -1
    return [...operators].sort((a, b) => {
      const diff = metricValue(a, sortKey) - metricValue(b, sortKey)
      if (diff !== 0) return diff * factor
      return a.fullName.localeCompare(b.fullName)
    })
  }, [operators, sortKey, sortDir])

  const shareBase = useMemo(
    () => sorted.reduce((sum, op) => sum + metricValue(op, sortKey), 0),
    [sorted, sortKey],
  )

  const summary = useMemo(() => {
    const totalSeconds = operators.reduce((sum, o) => sum + o.totalSeconds, 0)
    const activeDays = operators.reduce((sum, o) => sum + o.activeDays, 0)
    const totalHeartbeats = operators.reduce((sum, o) => sum + o.totalHeartbeats, 0)
    return {
      operatorCount: operators.length,
      totalSeconds,
      activeDays,
      totalHeartbeats,
      avgSecondsPerOperatorDay: activeDays > 0 ? totalSeconds / activeDays : 0,
    }
  }, [operators])

  const handleDownloadPdf = async () => {
    if (operators.length === 0) return
    setDownloading(true)
    try {
      const doc = new jsPDF("p", "mm", "a4")
      await loadPdfFont(doc)
      doc.setFont("DejaVuSans")

      const dateStr = new Date().toLocaleDateString("de-DE")
      doc.setFontSize(16)
      doc.text(t("activity_report_title"), 14, 15)
      doc.setFontSize(10)
      doc.setTextColor(120)
      doc.text(
        `${t("activity_report_period")}: ${range}d · ${t("activity_report_generated")}: ${dateStr}`,
        14,
        22,
      )
      doc.setTextColor(0)

      const summaryRows = [
        [t("activity_total_operators"), String(summary.operatorCount)],
        [t("activity_total_hours"), formatDuration(summary.totalSeconds)],
        [t("activity_active_days"), String(summary.activeDays)],
        [t("activity_total_heartbeats"), String(summary.totalHeartbeats)],
        [t("activity_avg_hours_operator_day"), formatDuration(summary.avgSecondsPerOperatorDay)],
      ]

      autoTable(doc, {
        startY: 28,
        head: [[t("activity_report_summary"), ""]],
        body: summaryRows,
        styles: { fontSize: 9, cellPadding: 2.5, font: "DejaVuSans", fontStyle: "normal" },
        headStyles: {
          fillColor: [50, 50, 50],
          font: "DejaVuSans",
          fontStyle: "normal",
        },
        columnStyles: { 0: { cellWidth: 70 } },
        theme: "grid",
      })

      const operatorRows = sorted.map((op, i) => [
        `#${i + 1}`,
        op.fullName,
        String(op.activeDays),
        formatDuration(op.totalSeconds),
        formatDuration(op.avgSecondsPerDay),
        String(op.totalHeartbeats),
      ])

      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 8,
        head: [
          [
            t("activity_rank"),
            t("activity_worker"),
            t("activity_active_days"),
            t("activity_total_hours"),
            t("activity_avg_hours_day"),
            t("activity_total_heartbeats"),
          ],
        ],
        body: operatorRows,
        styles: { fontSize: 8, cellPadding: 2.5, font: "DejaVuSans", fontStyle: "normal" },
        headStyles: {
          fillColor: [50, 50, 50],
          font: "DejaVuSans",
          fontStyle: "normal",
        },
        theme: "grid",
      })

      for (const op of sorted) {
        if (op.days.length === 0) continue
        let y = (doc as any).lastAutoTable.finalY + 10
        if (y > 260) {
          doc.addPage()
          y = 20
        }
        doc.setFontSize(10)
        doc.setTextColor(0)
        doc.text(`${op.fullName} — ${t("activity_hours_by_day")}`, 14, y - 2)
        autoTable(doc, {
          startY: y,
          head: [
            [
              t("activity_date"),
              t("activity_work_window"),
              t("activity_hours"),
              t("activity_heartbeats"),
            ],
          ],
          body: op.days.map((d) => [
            d.date,
            `${formatTime(d.firstSeenAt)}–${formatTime(d.lastSeenAt)}`,
            formatDuration(d.seconds),
            String(d.heartbeatCount),
          ]),
          styles: { fontSize: 8, cellPadding: 2, font: "DejaVuSans", fontStyle: "normal" },
          headStyles: {
            fillColor: [90, 90, 90],
            font: "DejaVuSans",
            fontStyle: "normal",
          },
          theme: "grid",
          columnStyles: {
            0: { cellWidth: 30 },
            1: { cellWidth: 45 },
            2: { cellWidth: 35 },
            3: { cellWidth: 30 },
          },
        })
      }

      doc.save(`worker_activity_report_${range}d_${dateStr}.pdf`)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("activity_report_title")}</DialogTitle>
          <p className="text-sm text-muted-foreground">{t("activity_report_hint")}</p>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`h-7 rounded-md border px-2.5 text-xs font-medium transition-colors ${
                  range === r
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {r}d
              </button>
            ))}
          </div>
          <button
            onClick={handleDownloadPdf}
            disabled={downloading || operators.length === 0}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            {t("activity_download_pdf")}
          </button>
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{t("loading")}</div>
        ) : operators.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{t("activity_empty")}</div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Tile label={t("activity_total_operators")} value={String(summary.operatorCount)} />
              <Tile label={t("activity_total_hours")} value={formatDuration(summary.totalSeconds)} />
              <Tile label={t("activity_active_days")} value={String(summary.activeDays)} />
              <Tile
                label={t("activity_avg_hours_operator_day")}
                value={formatDuration(summary.avgSecondsPerOperatorDay)}
              />
            </div>

            <div className="rounded-lg border">
              <div className="grid grid-cols-[2.5rem_1.6fr_3.5rem_4.5rem_4.5rem_4rem_3rem_2rem] items-center gap-2 border-b px-4 py-2 text-xs font-medium text-muted-foreground">
                <span>{t("activity_rank")}</span>
                <span>{t("activity_worker")}</span>
                <SortHeader
                  label={t("activity_active_days")}
                  active={sortKey === "activeDays"}
                  dir={sortDir}
                  onClick={() => toggleSort("activeDays")}
                />
                <SortHeader
                  label={t("activity_total_hours")}
                  active={sortKey === "hours"}
                  dir={sortDir}
                  onClick={() => toggleSort("hours")}
                />
                <SortHeader
                  label={t("activity_avg_hours_day")}
                  active={sortKey === "avg"}
                  dir={sortDir}
                  onClick={() => toggleSort("avg")}
                />
                <SortHeader
                  label={t("activity_requests")}
                  active={sortKey === "requests"}
                  dir={sortDir}
                  onClick={() => toggleSort("requests")}
                />
                <span className="text-right">{t("activity_share")}</span>
                <span />
              </div>

              <div className="max-h-[55vh] overflow-y-auto">
                {sorted.map((op, i) => {
                  const share =
                    shareBase > 0 ? Math.round((metricValue(op, sortKey) / shareBase) * 100) : 0
                  const isOpen = expanded === op.userId
                  return (
                    <div key={op.userId} className="border-b last:border-b-0">
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : op.userId)}
                        className="grid w-full grid-cols-[2.5rem_1.6fr_3.5rem_4.5rem_4.5rem_4rem_3rem_2rem] items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-accent/50"
                      >
                        <span
                          className={`font-medium ${
                            i === 0
                              ? "text-amber-500"
                              : i === 1
                                ? "text-slate-400"
                                : i === 2
                                  ? "text-orange-400"
                                  : "text-muted-foreground"
                          }`}
                        >
                          #{i + 1}
                        </span>
                        <span className="flex min-w-0 items-center gap-1.5">
                          <span className="truncate font-medium">{op.fullName}</span>
                          <span className="truncate text-xs text-muted-foreground">
                            {op.email}
                          </span>
                        </span>
                        <span className="text-right text-muted-foreground">{op.activeDays}</span>
                        <span className="text-right font-medium">
                          {formatDuration(op.totalSeconds)}
                        </span>
                        <span className="text-right text-muted-foreground">
                          {formatDuration(op.avgSecondsPerDay)}
                        </span>
                        <span className="text-right text-muted-foreground">
                          {op.totalHeartbeats}
                        </span>
                        <span className="text-right text-muted-foreground">{share}%</span>
                        <span className="flex justify-end text-muted-foreground">
                          {isOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </span>
                      </button>

                      {isOpen && (
                        <div className="border-t bg-muted/30 px-4 py-3">
                          <div className="mb-2 text-xs font-medium text-muted-foreground">
                            {t("activity_hours_by_day")}
                          </div>
                          {op.days.length === 0 ? (
                            <div className="py-3 text-center text-xs text-muted-foreground">
                              {t("activity_no_days")}
                            </div>
                          ) : (
                            <div className="overflow-hidden rounded-md border bg-background">
                              <div className="grid grid-cols-[5rem_1fr_5rem_4rem] gap-2 border-b px-3 py-1.5 text-xs font-medium text-muted-foreground">
                                <span>{t("activity_date")}</span>
                                <span>{t("activity_work_window")}</span>
                                <span className="text-right">{t("activity_hours")}</span>
                                <span className="text-right">{t("activity_heartbeats")}</span>
                              </div>
                              <div className="max-h-56 overflow-y-auto">
                                {op.days.map((d) => (
                                  <div
                                    key={d.date}
                                    className="grid grid-cols-[5rem_1fr_5rem_4rem] gap-2 border-b px-3 py-1.5 text-xs last:border-b-0"
                                  >
                                    <span className="font-mono text-muted-foreground">
                                      {shortDate(d.date)}
                                    </span>
                                    <span className="text-muted-foreground">
                                      {formatTime(d.firstSeenAt)}–{formatTime(d.lastSeenAt)}
                                    </span>
                                    <span className="text-right font-medium">
                                      {formatHours(d.seconds)}
                                    </span>
                                    <span className="text-right text-muted-foreground">
                                      {d.heartbeatCount}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-lg font-semibold" title={value}>
        {value}
      </p>
    </div>
  )
}

function SortHeader({
  label,
  active,
  dir,
  onClick,
}: {
  label: string
  active: boolean
  dir: SortDir
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-end gap-1 text-right transition-colors hover:text-foreground ${
        active ? "text-foreground" : ""
      }`}
    >
      {label}
      {active ? (
        dir === "asc" ? (
          <ArrowUp className="h-3.5 w-3.5" />
        ) : (
          <ArrowDown className="h-3.5 w-3.5" />
        )
      ) : (
        <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
      )}
    </button>
  )
}

export default WorkerActivityReportModal
